import React from 'react';
import {
  AmcPricingDTO,
  AmcRemainingCostDTO,
  CardHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CardSubSection,
  IconChevronDown,
  IconChevronUp,
} from '@maiia/design-system';
import { actionsGeneratorV2, asyncActions } from '@docavenue/core';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from '@docavenue/components';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/src/i18n';

import Refunds from './Refunds';
import useRemainingCostInsuranceContract from '@/src/hooks/insurances/useRemainingCostInsuranceContract';
import { formatPricing } from './utils';

type Props = {
  card: CardHcd;
};

// TODO: maybe overkill to use actionsGeneratorV2 ?
const amcRemainingCostAction = actionsGeneratorV2({
  resource: 'amc-remaining-cost',
  scope: 'pat',
});

const VISIBLE_ITEMS_COUNT = 3;

const Tarif = ({ card }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [showAll, setShowAll] = React.useState(false);
  const [pricesList, setPricesList] = React.useState<AmcPricingDTO[]>(
    card.publicInformation?.pricing || [],
  );
  const { data: amcContract } = useRemainingCostInsuranceContract(card.id);

  const hasConventionedLine =
    pricesList.findIndex(
      o => o.conventionedActId && o.conventionedActId.length > 0,
    ) !== -1;

  const hasUnconventionedLine =
    pricesList.findIndex(
      o => !o.conventionedActId || o.conventionedActId.length === 0,
    ) !== -1;

  const isAmcRemainingCostCompatible =
    amcContract?.isAmcRemainingCostCompatible === true;

  const [
    isRemainingCostColumnVisible,
    setIsRemainingCostColumnVisible,
  ] = React.useState(false);

  const showUnconventionedNotice =
    hasUnconventionedLine && isRemainingCostColumnVisible;

  const visiblePriceItems = React.useMemo(() => {
    const sortedList = pricesList.sort((a, b) => {
      if (a.conventionedActId === undefined) return 1;
      if (a.conventionedActId && b.conventionedActId) return 0;
      return -1;
    });
    return showAll ? sortedList : sortedList.slice(0, VISIBLE_ITEMS_COUNT);
  }, [showAll, pricesList]);

  const hiddenPriceItemsCount = showAll
    ? 0
    : pricesList.length - visiblePriceItems.length;

  const noPriceLabel = (() => {
    switch (card.type) {
      case 'CENTER':
        return t('center-did-not-provide-his-prices');
      case 'PHARMACY':
        return t('pharmacy-did-not-provide-his-prices');
      case 'PRACTITIONER':
        return t('practitioner-did-not-provide-his-prices');
      default:
        return t('prices-not-provided');
    }
  })();

  const { isLoading, isFetching } = useQuery({
    queryKey: ['pricesList', card.id, currentUser?.id],
    enabled: !!(card.id && currentUser?.id && isAmcRemainingCostCompatible),
    refetchOnWindowFocus: false,
    queryFn: async () =>
      asyncActions<AmcRemainingCostDTO>(
        dispatch,
        amcRemainingCostAction.getOneWithCompositeKey({
          cardHcdId: card.id,
        }),
      ),
    onSuccess: data => {
      if (data.pricing) {
        const hasRemainingCostDefined =
          data.pricing.findIndex(
            o => o.remainingCost || o.remainingCost === 0,
          ) !== -1;
        setPricesList(data.pricing);
        setIsRemainingCostColumnVisible(hasRemainingCostDefined);
      } else {
        setIsRemainingCostColumnVisible(false);
      }
    },
    onError: () => {
      setIsRemainingCostColumnVisible(false);
    },
  });

  // Define remaining cost column visibility on loading
  React.useEffect(() => {
    if (isLoading) {
      setIsRemainingCostColumnVisible(
        !!(hasConventionedLine && currentUser && isAmcRemainingCostCompatible),
      );
    }
  }, [
    hasConventionedLine,
    currentUser,
    isAmcRemainingCostCompatible,
    isLoading,
  ]);

  return (
    <>
      <Refunds card={card} />

      <CardSubSection
        title={t('rates')}
        id="tarifs"
        data-testid="rates-section"
      >
        {visiblePriceItems.length > 0 ? (
          <Stack spacing={1}>
            <TableContainer component={Box}>
              <Table
                sx={{
                  width: '100%',
                  '& .MuiTableCell-root:first-of-type': { pl: 0 },
                  '& .MuiTableCell-root:last-of-type': { pr: 0 },
                  '& > .MuiTableBody-root *': {
                    fontSize: theme.typography.body1.fontSize,
                  },
                  '& th': {
                    wordBreak: 'keep-all',
                  },
                }}
                aria-label={t('rates')}
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component="span"
                        color="info.light"
                      >
                        {t('medical-procedures')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        component="span"
                        color="info.light"
                      >
                        {t('rates')}
                      </Typography>
                    </TableCell>
                    {isRemainingCostColumnVisible && (
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          component="span"
                          color="info.light"
                        >
                          {t('rest-to-pay')}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePriceItems.map(pricing => {
                    const formattedPricing = formatPricing(pricing, t);
                    const hasRemainingCost =
                      pricing.remainingCost !== undefined;
                    const isConventioned = pricing.conventionedActId;
                    return (
                      <TableRow
                        key={`pricing_${pricing.label}`}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {pricing.label}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formattedPricing}
                        </TableCell>
                        {isRemainingCostColumnVisible && (
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              ...(hasRemainingCost && {
                                color: 'primary.main',
                              }),
                            }}
                          >
                            {isFetching && isConventioned ? (
                              <Box
                                sx={{
                                  // Mui loader is rotating so we can't push it to the right
                                  // because depending on system or browser settings
                                  // its rotation will cause the horizontal scrollbar to blink
                                  overflow: 'hidden',
                                }}
                              >
                                <CircularProgress color="secondary" size={15} />
                              </Box>
                            ) : (
                              <>
                                {hasRemainingCost
                                  ? t('price', { value: pricing.remainingCost })
                                  : 'NA*'}
                              </>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {showUnconventionedNotice && (
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                {t('unconventioned-caption')}
              </Typography>
            )}

            {pricesList.length > VISIBLE_ITEMS_COUNT && (
              <Button
                variant="text"
                onClick={() => setShowAll(!showAll)}
                endIcon={
                  showAll ? (
                    <IconChevronUp aria-hidden="true" width="10px" />
                  ) : (
                    <IconChevronDown aria-hidden="true" width="10px" />
                  )
                }
                sx={{
                  minWidth: 'unset',
                  padding: 0,
                  border: 'unset',
                  alignSelf: 'flex-start',
                  textTransform: 'lowercase',
                }}
              >
                {showAll
                  ? t('show_less')
                  : `${t('show_more')} (${hiddenPriceItemsCount})`}
              </Button>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" component="span">
            {noPriceLabel}
          </Typography>
        )}
      </CardSubSection>
    </>
  );
};
export default Tarif;
